/**
 * EngineActionPlanCard — unified hybrid action-plan card for the
 * People Engine / ROE / ROI Health pages.
 *
 * Same UX contract as the Maturity & LEI action-plan cards but
 * parameterized by `engine` so the three Health pages share one
 * component instead of triplicating it. Each engine has its own
 * tone palette and natural-language framing of the entity.
 *
 * Props
 * ─────
 *   • engine        : 'pe' | 'roe' | 'roi'
 *   • params        : engine-specific query params (e.g. {component: 'capability'} for PE,
 *                     {metric: 'l3_behavior'} for ROE, {scenario_id: '...'} for ROI)
 *   • title         : optional heading override (defaults to "Action plan: <entity>")
 *
 * The component owns its own data fetch, refresh, and pin-to-deck
 * flow — parent just decides which engine + entity to focus on.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Sparkles, ClipboardCheck, RefreshCw, AlertTriangle, Loader2,
  Calendar, User, Pin, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import logger from '../../lib/logger';
import { useNavigate } from 'react-router-dom';
import { TakeActionButton, buildFocusFromEntity } from '../health/HealthActionSpine';

const ENGINE_CONFIG = {
  pe: {
    path: '/pe-action-plan',
    pinTheme: 'pe-action',
    pinTitlePrefix: 'PE Action',
    pinPath: '/people-engine/health',
    headerColor: 'text-violet-600',
    metricLabel: 'Score',
    metricSuffix: '/100',
  },
  roe: {
    path: '/roe-action-plan',
    pinTheme: 'roe-action',
    pinTitlePrefix: 'ROE Action',
    pinPath: '/roe-health',
    headerColor: 'text-amber-600',
    metricLabel: 'Variance',
    // Variance can be negative, so we render it with a sign; the
    // suffix is "pts" not "/100".
    metricSuffix: ' pts',
    signed: true,
  },
  roi: {
    path: '/roi-action-plan',
    pinTheme: 'roi-action',
    pinTitlePrefix: 'ROI Action',
    pinPath: '/roi-health',
    headerColor: 'text-emerald-600',
    metricLabel: 'ROI',
    metricSuffix: 'x',
  },
};

const PIN_DESCRIPTION_LIMIT = 400;

const buildPinDescription = (data, fallbackEntity) => {
  const ai = data?.ai_context?.text;
  const fallback = `Action plan for ${data?.entity_label || fallbackEntity}.`;
  const text = ai && ai.trim() ? ai : fallback;
  return text.length <= PIN_DESCRIPTION_LIMIT
    ? text
    : text.slice(0, PIN_DESCRIPTION_LIMIT - 1).trimEnd() + '…';
};

const buildPinStats = (data, cfg) => {
  const stats = [];
  if (typeof data?.score === 'number') {
    const formatted = cfg.signed && data.score >= 0
      ? `+${data.score}${cfg.metricSuffix}`
      : `${data.score}${cfg.metricSuffix}`;
    stats.push({ label: cfg.metricLabel, value: formatted });
  }
  if (typeof data?.overall_score === 'number') {
    stats.push({
      label: 'Overall',
      value: cfg.metricSuffix === 'x'
        ? `${data.overall_score}x`
        : `${Math.round(data.overall_score)}${cfg.metricSuffix.replace(' pts', '')}`,
    });
  }
  const firstAction = data?.actions?.[0];
  if (firstAction?.title) {
    stats.push({
      label: 'First move',
      value: firstAction.title.length > 26
        ? firstAction.title.slice(0, 25) + '…'
        : firstAction.title,
      hint: firstAction.horizon ? `Horizon: ${firstAction.horizon}` : undefined,
    });
  }
  return stats;
};

const formatScore = (val, cfg) => {
  if (typeof val !== 'number') return '—';
  if (cfg.metricSuffix === 'x') return `${val}${cfg.metricSuffix}`;
  if (cfg.signed) {
    const sign = val >= 0 ? '+' : '';
    return `${sign}${val.toFixed(1)}${cfg.metricSuffix}`;
  }
  return `${Math.round(val)}${cfg.metricSuffix}`;
};

export default function EngineActionPlanCard({ engine, params, title }) {
  const cfg = ENGINE_CONFIG[engine];
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [pinning, setPinning] = useState(false);
  const [pinned, setPinned] = useState(false);

  const queryString = useMemo(() => {
    if (!params) return '';
    return Object.entries(params)
      .filter(([_, v]) => v != null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');
  }, [params]);

  const load = useCallback(async () => {
    if (!cfg || !queryString) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`${cfg.path}?${queryString}`);
      setData(res.data);
    } catch (e) {
      logger.error(`${engine} action plan load failed:`, e);
      setError(e?.response?.data?.detail || 'Could not load action plan');
    } finally {
      setLoading(false);
    }
  }, [cfg, queryString, engine]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPinned(false); }, [queryString]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const handlePin = async () => {
    if (!data || pinning) return;
    setPinning(true);
    try {
      await api.post('/quarterly-deck/pins', {
        path: cfg.pinPath,
        title: `${cfg.pinTitlePrefix}: ${data.entity_label || 'weakest area'}`,
        description: buildPinDescription(data, data.entity_label),
        theme: cfg.pinTheme,
        stats: buildPinStats(data, cfg),
        note: data.ai_context?.text
          ? data.ai_context.text.slice(0, 400)
          : null,
      });
      setPinned(true);
      toast.success('Action plan pinned to Quarterly Deck');
    } catch (err) {
      logger.error(`${engine} action plan pin failed:`, err);
      toast.error('Could not pin — try again');
    } finally {
      setPinning(false);
    }
  };

  if (!cfg) return null;

  return (
    <Card data-testid={`${engine}-action-plan-card`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <ClipboardCheck className={`h-5 w-5 ${cfg.headerColor}`} />
            {title || `Action plan: ${data?.entity_label || `your weakest ${engine.toUpperCase()} area`}`}
          </CardTitle>
          <div className="flex items-center gap-1">
            <TakeActionButton
              focus={buildFocusFromEntity(engine, data)}
              navigate={navigate}
              testId={`${engine}-action-plan-take-action`}
            />
            <Button
              variant={pinned ? 'outline' : 'default'}
              size="sm"
              onClick={handlePin}
              disabled={pinning || !data || loading}
              className={pinned
                ? 'text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                : 'text-xs bg-slate-900 hover:bg-slate-800 text-white'
              }
              data-testid={`${engine}-action-plan-pin`}
            >
              {pinning ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : pinned ? (
                <Check className="h-3.5 w-3.5 mr-1" />
              ) : (
                <Pin className="h-3.5 w-3.5 mr-1" />
              )}
              {pinned ? 'Pinned to Deck' : 'Pin to Quarterly Deck'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={loading || refreshing}
              className="text-xs"
              data-testid={`${engine}-action-plan-refresh`}
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
        {data && data.score != null && (
          <p className="text-xs text-slate-500 mt-1">
            {cfg.metricLabel} <strong>{formatScore(data.score, cfg)}</strong>
            {data.overall_score != null && (
              <> · org overall {formatScore(data.overall_score, cfg)}</>
            )}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && !data && (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-4" data-testid={`${engine}-action-plan-loading`}>
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading personalized action plan…
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3" data-testid={`${engine}-action-plan-error`}>
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        {data && (
          <>
            {data.ai_context?.text ? (
              <div
                className="bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200 rounded-md p-4"
                data-testid={`${engine}-action-plan-ai-context`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-3.5 w-3.5 text-violet-600" />
                  <span className="text-[11px] uppercase tracking-wider font-bold text-violet-700">
                    Personalized coaching
                  </span>
                  {data.ai_context.cached && (
                    <Badge variant="outline" className="text-[9px] py-0 px-1.5 text-violet-600 border-violet-300">
                      cached
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-slate-800 leading-relaxed">
                  {data.ai_context.text}
                </p>
              </div>
            ) : data.ai_context_status === 'no_data' ? (
              <div className="text-xs text-slate-500 italic" data-testid={`${engine}-action-plan-no-data`}>
                Personalized coaching will appear once {engine.toUpperCase()} data is captured for your org.
              </div>
            ) : (
              <div className="text-xs text-slate-500 italic" data-testid={`${engine}-action-plan-ai-degraded`}>
                Personalized coaching is temporarily unavailable. Static action plan below is still your best next-step.
              </div>
            )}

            <div>
              <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">
                Your next 3 moves
              </p>
              <ol className="space-y-2.5" data-testid={`${engine}-action-plan-actions`}>
                {(data.actions || []).map((a, i) => (
                  <li
                    key={a.title}
                    className="flex gap-3 p-3 rounded-md border border-slate-200 hover:border-slate-300 transition-colors"
                    data-testid={`${engine}-action-${i + 1}`}
                  >
                    <div className="shrink-0 w-7 h-7 rounded-full bg-slate-900 text-white text-sm font-bold flex items-center justify-center">
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{a.title}</p>
                      <p className="text-[12px] text-slate-600 mt-0.5 leading-snug">
                        {a.description}
                      </p>
                      <div className="flex flex-wrap items-center gap-3 mt-1.5 text-[11px] text-slate-500">
                        {a.owner && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {a.owner}
                          </span>
                        )}
                        {a.horizon && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {a.horizon}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
